import { parseUserDefinedFixingSystemId } from '../../app/defaultProject';
import type { Project, RoofModule } from '../../gen/ejot/roofing/v1/types';
import type { CatalogueSelectionState, CompleteSystemSource } from '../catalogue/CatalogueSelection';
import { fromContract, toContract, type GeometryModel } from '../roof-plan-editor/model';

export interface OpenProjectState {
  project: Project;
  geometry: GeometryModel;
  catalogue: CatalogueSelectionState;
}

export interface ModuleUiState {
  geometry: GeometryModel;
  catalogue: CatalogueSelectionState;
}

/** Restores editor/catalogue state for one canonical roof module. */
export function stateFromModule(module: RoofModule | undefined): ModuleUiState {
  const fixingSystem = module?.fixing_system;
  const etaMode = fixingSystem?.resistance_basis === 'FIXING_RESISTANCE_BASIS_ETA_COMPONENT_CHARACTERISTIC';
  const userDefinedSystem = parseUserDefinedFixingSystemId(fixingSystem?.fixing_combination_id);
  const userDefined = !etaMode && Boolean(userDefinedSystem) && Boolean(fixingSystem?.custom_membrane);
  const catalogueCompleteSystem = !etaMode
    && !userDefined
    && Boolean(fixingSystem?.membrane_product_id)
    && Boolean(fixingSystem?.fixing_combination_id);
  const source: CompleteSystemSource | undefined = userDefined
    ? 'USER_DEFINED'
    : catalogueCompleteSystem
      ? 'CATALOGUE'
      : undefined;
  const section = module?.build_up?.section;
  return {
    geometry: fromContract(module?.build_up?.plan),
    catalogue: {
      substrateCategory: section?.substrate_category,
      membraneProductId: userDefined ? undefined : fixingSystem?.membrane_product_id,
      fixingCombinationId: userDefined || etaMode ? undefined : fixingSystem?.fixing_combination_id,
      totalInsulationThicknessMm: section?.total_insulation_thickness?.millimetres
        ?? section?.base_insulation_thickness?.millimetres,
      etaReference: fixingSystem?.eta_reference,
      fixingBasis: fixingSystem?.resistance_basis,
      completeSystemSource: source,
      userDefinedMembraneBrand: fixingSystem?.custom_membrane?.manufacturer || fixingSystem?.custom_membrane?.display_name,
      userDefinedMembraneType: fixingSystem?.custom_membrane?.membrane_family,
      userDefinedMembraneThicknessMm: fixingSystem?.custom_membrane?.thickness?.millimetres,
      userDefinedFixingSystemName: userDefined ? userDefinedSystem?.name : undefined,
      userDefinedCharacteristicResistanceKn: userDefined
        ? userDefinedSystem?.characteristicResistanceKn
        : undefined,
    },
  };
}

/** Restores UI selection state from the canonical persisted project only. */
export function stateFromProject(project: Project): OpenProjectState {
  const moduleState = stateFromModule(project.modules?.[0]);
  return {
    project,
    ...moduleState,
  };
}

/** Creates a Save As payload without copying server-owned identity/timestamps. */
export function asUnsavedCopy(project: Project): Project {
  return {
    ...project,
    id: undefined,
    created_at: undefined,
    updated_at: undefined,
    created_by: undefined,
    archived: false,
  };
}

/**
 * Prevents merely opening the editor from making a project dirty.
 * Both sides are normalized through the editor contract so omitted Proto-JSON
 * default fields do not turn an unchanged persisted plan into a false edit.
 */
export function geometryMatchesProject(project: Project, geometry: GeometryModel): boolean {
  const persisted = project.modules?.[0]?.build_up?.plan;
  const normalizedPersisted = toContract(fromContract(persisted));
  return JSON.stringify(normalizedPersisted) === JSON.stringify(toContract(geometry));
}
